/**
 * /extrafun — the room in the house with the disco ball.
 *
 * Auto-mounted at /extrafun by the pages routing convention (src/pages/<name>.tsx).
 * Filed as #24: "confetti and different games. It should be very crazy."
 *
 * DESIGN. Every other screen in SPZ is deliberately sober — muted teal, dense with
 * dates and drive times, built to be read on a phone in a group chat argument. This
 * one is its opposite on purpose; the contrast is the joke. The direction is 1970s
 * roadside-motel Americana, because that is the actual world of an annual family
 * road trip: marquee bulbs, asphalt at night, sunbaked orange and motel turquoise.
 *
 * Deliberately dark-only. A neon sign in daylight is just a sign, so this page does
 * not follow the app's light/dark scheme — it commits, and paints its own ground.
 *
 * NO DEPENDENCIES, NO ASSETS, NO AUDIO. Type is system faces (Impact is the motel
 * marquee; monospace is the odometer), art is CSS and emoji, confetti is a canvas.
 * Nothing here downloads anything.
 *
 * ACCESSIBILITY. The whole page is motion. `prefers-reduced-motion` turns off the
 * confetti and stops the bulbs chasing, and the page says it has done so rather
 * than appearing broken. Every control is a real button, reachable by keyboard,
 * with a visible focus ring and a 44px target.
 */
import { useCallback, useEffect, useRef, useState, type JSX } from 'react'
import { Confetti, prefersReducedMotion } from '../components/Confetti'
import {
  CATS,
  conflicts,
  cycle,
  generatePuzzle,
  isSolved,
  type Grid,
  type Puzzle,
} from '../lib/meowdoku'

/* ------------------------------------------------------------------ tokens -- */

const PALETTE = ['#F25C05', '#00B2A9', '#FF3D7F', '#F2B705', '#FFF3E0']

const CSS = `
.pz {
  --pz-night: #1A1526;
  --pz-asphalt: #241d33;
  --pz-cream: #FFF3E0;
  --pz-orange: #F25C05;
  --pz-turq: #00B2A9;
  --pz-pink: #FF3D7F;
  --pz-mustard: #F2B705;
  --pz-display: Impact, 'Haettenschweiler', 'Arial Narrow Bold', 'Arial Black', system-ui, sans-serif;
  --pz-meter: ui-monospace, SFMono-Regular, Menlo, monospace;

  position: relative;
  /* Above the confetti canvas (z-index 1), which is itself above .pz-bg (z-index 0).
     Deliberately NOT painted here: an opaque background on this element hides the
     canvas behind it, and the confetti stops existing. .pz-bg owns the ground. */
  z-index: 2;
  min-height: 100dvh;
  background: transparent;
  color: var(--pz-cream);
  padding: 1.5rem 1rem calc(3rem + env(safe-area-inset-bottom));
  display: flex;
  flex-direction: column;
  gap: 1.75rem;
  align-items: center;
  overflow-x: hidden;
}
/* The page paints its own ground behind the fixed confetti canvas. */
.pz-bg {
  position: fixed; inset: 0; z-index: 0; background: var(--pz-night);
}
.pz-disco { animation: pz-hue 14s linear infinite; }
@keyframes pz-hue { to { filter: hue-rotate(360deg); } }

.pz > * { width: 100%; max-width: 46rem; }

/* ---- the sign ---- */
.pz-sign {
  border: 4px solid var(--pz-mustard);
  border-radius: 1.25rem;
  background: var(--pz-asphalt);
  padding: 0.75rem 1rem 1rem;
  text-align: center;
  box-shadow: 0 0 0 4px var(--pz-night), 0 0 44px rgba(242, 183, 5, 0.28);
}
.pz-bulbs { display: flex; justify-content: space-between; padding: 0 0.25rem; }
.pz-bulb {
  width: 9px; height: 9px; border-radius: 50%;
  background: var(--pz-mustard); opacity: 0.28;
  animation: pz-chase 1.4s linear infinite;
}
@keyframes pz-chase {
  0%, 70% { opacity: 0.22; box-shadow: none; }
  12% { opacity: 1; box-shadow: 0 0 10px var(--pz-mustard); }
}
.pz-eyebrow {
  font-family: var(--pz-meter);
  font-size: 0.7rem; letter-spacing: 0.34em; text-transform: uppercase;
  color: var(--pz-turq); margin: 0.5rem 0 0.1rem;
}
.pz-title {
  font-family: var(--pz-display);
  font-size: clamp(2.9rem, 17vw, 6rem);
  line-height: 0.86;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: var(--pz-cream);
  text-shadow: 3px 3px 0 var(--pz-pink), 6px 6px 0 var(--pz-orange);
}
.pz-vacancy {
  display: inline-block; margin-top: 0.6rem;
  font-family: var(--pz-meter); font-size: 0.72rem; letter-spacing: 0.2em;
  text-transform: uppercase; color: var(--pz-pink);
  border: 1px solid var(--pz-pink); border-radius: 999px; padding: 0.2rem 0.7rem;
}

/* ---- controls ---- */
.pz-controls { display: flex; flex-wrap: wrap; gap: 0.6rem; justify-content: center; }
.pz-btn {
  font-family: var(--pz-meter);
  font-size: 0.8rem; letter-spacing: 0.1em; text-transform: uppercase; font-weight: 700;
  min-height: 44px; padding: 0.6rem 1.1rem;
  border-radius: 999px; border: 2px solid var(--pz-turq);
  background: transparent; color: var(--pz-turq);
}
.pz-btn[aria-pressed='true'] { background: var(--pz-turq); color: var(--pz-night); }
.pz-btn-cannon { border-color: var(--pz-pink); color: var(--pz-night); background: var(--pz-pink); }
.pz-btn:focus-visible, .pz-cell:focus-visible, .pz-big:focus-visible {
  outline: 3px solid var(--pz-mustard); outline-offset: 3px;
}

/* ---- game cards ---- */
.pz-games { display: grid; gap: 1rem; grid-template-columns: 1fr; }
@media (min-width: 44rem) { .pz-games { grid-template-columns: 1fr 1fr; } }
.pz-card {
  border: 2px solid rgba(255, 243, 224, 0.16);
  border-radius: 1rem; background: var(--pz-asphalt);
  padding: 1rem; display: flex; flex-direction: column; gap: 0.75rem;
}
.pz-card h2 {
  font-family: var(--pz-display); font-size: 1.6rem; letter-spacing: 0.02em;
  text-transform: uppercase; color: var(--pz-mustard);
}
.pz-hint { font-size: 0.85rem; color: rgba(255, 243, 224, 0.66); }
.pz-score {
  font-family: var(--pz-meter); font-size: 0.78rem; letter-spacing: 0.08em;
  text-transform: uppercase; color: var(--pz-turq);
}
.pz-big {
  font-family: var(--pz-display); text-transform: uppercase; letter-spacing: 0.04em;
  font-size: 1.15rem; min-height: 52px; border: none; border-radius: 0.6rem;
  background: var(--pz-orange); color: var(--pz-night);
}
.pz-big[disabled] { opacity: 0.45; }

/* ---- pack the car ---- */
.pz-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 0.5rem; }
.pz-cell {
  aspect-ratio: 1; border-radius: 0.6rem; border: 2px dashed rgba(255, 243, 224, 0.14);
  background: rgba(0, 0, 0, 0.25); font-size: clamp(1.5rem, 7vw, 2.2rem);
  display: flex; align-items: center; justify-content: center; padding: 0;
}
.pz-cell-full { border-style: solid; border-color: var(--pz-turq); }
.pz-shake { animation: pz-shake 0.3s; }
@keyframes pz-shake {
  25% { transform: translateX(-6px); } 75% { transform: translateX(6px); }
}

/* ---- roulette ---- */
.pz-reels {
  font-family: var(--pz-display); text-transform: uppercase;
  font-size: clamp(1.1rem, 4.5vw, 1.5rem); line-height: 1.25;
  min-height: 5.5rem; display: flex; flex-direction: column; justify-content: center;
  text-align: center; border-radius: 0.6rem; padding: 0.75rem;
  background: rgba(0,0,0,0.3);
}
.pz-reel-1 { color: var(--pz-turq); }
.pz-reel-2 { color: var(--pz-cream); }
.pz-reel-3 { color: var(--pz-pink); }

/* ---- meowdoku ---- */
.pz-meow { display: grid; grid-template-columns: repeat(4, 1fr); gap: 3px; background: rgba(255,243,224,0.16); padding: 3px; border-radius: 0.6rem; }
.pz-meow-cell {
  aspect-ratio: 1; border: none; padding: 0; border-radius: 0.25rem;
  background: rgba(0,0,0,0.3); color: var(--pz-cream);
  font-size: clamp(1.3rem, 6vw, 2rem); line-height: 1;
  display: flex; align-items: center; justify-content: center;
}
/* Thicker gutters on the box seams, so the four 2x2 boxes read as boxes. */
.pz-meow-cell:nth-child(4n+3) { margin-left: 2px; }
.pz-meow-cell:nth-child(n+9):nth-child(-n+12) { margin-top: 2px; }
.pz-meow-fixed { background: rgba(0,178,169,0.22); opacity: 1; }
.pz-meow-bad { box-shadow: inset 0 0 0 2px var(--pz-pink); }
.pz-meow-cell:focus-visible { outline: 3px solid var(--pz-mustard); outline-offset: 2px; }
.pz-meow-won { font-family: var(--pz-display); text-transform: uppercase; color: var(--pz-turq); font-size: 1.1rem; }

/* ---- tap-tap ---- */
/* Grows to fill the card. The two game cards sit in a grid row and stretch to the
   taller one, so without this the light floats at the top of a half-empty card. */
.pz-light {
  flex: 1;
  border-radius: 0.6rem; min-height: 8rem; display: flex; align-items: center;
  justify-content: center; text-align: center; padding: 1rem;
  font-family: var(--pz-display); text-transform: uppercase;
  font-size: clamp(1.1rem, 5vw, 1.6rem); border: none; width: 100%;
  background: rgba(0,0,0,0.3); color: var(--pz-cream);
}
.pz-light-wait { background: var(--pz-orange); color: var(--pz-night); }
.pz-light-go { background: #17c964; color: #04210f; }
.pz-light-foul { background: var(--pz-pink); color: var(--pz-night); }

/* ---- leaderboard ---- */
.pz-board { border-top: 2px solid rgba(255,243,224,0.16); padding-top: 1rem; }
.pz-board h2 {
  font-family: var(--pz-meter); font-size: 0.72rem; letter-spacing: 0.24em;
  text-transform: uppercase; color: rgba(255,243,224,0.6); margin-bottom: 0.6rem;
}
.pz-row {
  display: flex; justify-content: space-between; align-items: baseline; gap: 1rem;
  padding: 0.5rem 0; border-bottom: 1px dashed rgba(255,243,224,0.14);
}
.pz-row span:first-child { font-size: 0.9rem; }
.pz-odo {
  font-family: var(--pz-meter); font-size: 1.05rem; font-weight: 700;
  color: var(--pz-mustard); letter-spacing: 0.08em;
}
.pz-foot { text-align: center; font-size: 0.85rem; color: rgba(255,243,224,0.55); }
.pz-foot a { color: var(--pz-turq); }

@media (prefers-reduced-motion: reduce) {
  .pz-bulb, .pz-disco, .pz-shake { animation: none; }
}
`

/* ----------------------------------------------------------------- scores -- */

const STORE_KEY = 'spz.extrafun.v1'

interface Bests {
  packed: number
  reaction: number
  /** Fastest Meowdoku solve, in seconds. 0 = never solved. */
  meow: number
}

function loadBests(): Bests {
  try {
    const raw = localStorage.getItem(STORE_KEY)
    if (!raw) return { packed: 0, reaction: 0, meow: 0 }
    const parsed = JSON.parse(raw) as Partial<Bests>
    return {
      packed: typeof parsed.packed === 'number' ? parsed.packed : 0,
      reaction: typeof parsed.reaction === 'number' ? parsed.reaction : 0,
      // Added after v1 shipped, so an existing saved blob has no `meow` key.
      meow: typeof parsed.meow === 'number' ? parsed.meow : 0,
    }
  } catch {
    // Private browsing, disabled storage, or someone put junk in the key. A
    // broken high score must never take the page down with it.
    return { packed: 0, reaction: 0, meow: 0 }
  }
}

function saveBests(bests: Bests): void {
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify(bests))
  } catch {
    /* nothing to do — the games still work, the score just won't persist */
  }
}

/* -------------------------------------------------------------- pack the car -- */

/** Worth points. The cooler is worth double because obviously. */
const CARGO = ['🧳', '⛱️', '🩴', '🧴', '🍉', '🏐', '🐕', '📚', '🎣', '🧢']
const COOLER = '🧊'
const PEST = '🦟'
const ROUND_MS = 25_000
const SLOTS = 9

interface Occupant {
  emoji: string
  bornAt: number
}

function PackTheCar({
  best,
  onBest,
  onWin,
}: {
  best: number
  onBest: (score: number) => void
  onWin: () => void
}): JSX.Element {
  const [running, setRunning] = useState(false)
  const [score, setScore] = useState(0)
  const [left, setLeft] = useState(ROUND_MS)
  const [cells, setCells] = useState<(Occupant | null)[]>(() => Array(SLOTS).fill(null))
  const [shake, setShake] = useState(false)

  const start = useCallback(() => {
    setCells(Array(SLOTS).fill(null))
    setScore(0)
    setLeft(ROUND_MS)
    setRunning(true)
  }, [])

  // One interval drives spawning, expiry and the clock, so they can never drift
  // out of step with each other.
  useEffect(() => {
    if (!running) return
    const started = Date.now()

    const id = window.setInterval(() => {
      const elapsed = Date.now() - started
      const remaining = Math.max(0, ROUND_MS - elapsed)
      setLeft(remaining)

      if (remaining === 0) {
        setRunning(false)
        setCells(Array(SLOTS).fill(null))
        return
      }

      setCells((prev) => {
        const next = [...prev]
        // Expire anything that has been up too long. Gets faster as the round runs.
        const life = 1500 - Math.min(700, (elapsed / ROUND_MS) * 700)
        for (let i = 0; i < next.length; i++) {
          const occupant = next[i]
          if (occupant && Date.now() - occupant.bornAt > life) next[i] = null
        }
        const empty = next.map((c, i) => (c === null ? i : -1)).filter((i) => i >= 0)
        if (empty.length > 0 && Math.random() < 0.75) {
          const slot = empty[Math.floor(Math.random() * empty.length)]
          if (slot !== undefined) {
            const roll = Math.random()
            const emoji =
              roll < 0.14
                ? PEST
                : roll < 0.24
                  ? COOLER
                  : (CARGO[Math.floor(Math.random() * CARGO.length)] ?? '🧳')
            next[slot] = { emoji, bornAt: Date.now() }
          }
        }
        return next
      })
    }, 190)

    return () => window.clearInterval(id)
  }, [running])

  // Record the best only once the round is genuinely over.
  useEffect(() => {
    if (running || left > 0 || score <= 0) return
    if (score > best) {
      onBest(score)
      onWin()
    }
  }, [running, left, score, best, onBest, onWin])

  function tap(index: number): void {
    const occupant = cells[index]
    if (!occupant || !running) return
    setCells((prev) => {
      const next = [...prev]
      next[index] = null
      return next
    })
    if (occupant.emoji === PEST) {
      setScore((s) => Math.max(0, s - 2))
      setShake(true)
      window.setTimeout(() => setShake(false), 320)
    } else {
      setScore((s) => s + (occupant.emoji === COOLER ? 2 : 1))
    }
  }

  const done = !running && left === 0

  return (
    <section className={`pz-card${shake ? ' pz-shake' : ''}`}>
      <h2>Pack the car</h2>
      <p className="pz-hint">
        Tap everything before it wanders off. The cooler {COOLER} is worth two. Swat a
        mosquito {PEST} and you lose two, which is also true in life.
      </p>
      <p className="pz-score">
        Packed {score} · {(left / 1000).toFixed(1)}s left · Best {best}
      </p>

      <div className="pz-grid">
        {cells.map((cell, i) => (
          <button
            key={i}
            type="button"
            className={`pz-cell${cell ? ' pz-cell-full' : ''}`}
            onClick={() => tap(i)}
            disabled={!running}
            aria-label={cell ? `Pack ${cell.emoji}` : 'Empty space'}
          >
            {cell?.emoji ?? ''}
          </button>
        ))}
      </div>

      <button type="button" className="pz-big" onClick={start} disabled={running}>
        {running ? 'Packing…' : done ? 'Again' : 'Start packing'}
      </button>
      {done ? (
        <p className="pz-hint">
          {score > best
            ? `New record: ${score}. The car has never been packed so well.`
            : `${score} packed. The record still stands at ${best}.`}
        </p>
      ) : null}
    </section>
  )
}

/* ------------------------------------------------------------- roulette -- */

const PLACES = [
  'the Outer Banks',
  'a lake in Vermont',
  'the Jersey Shore',
  'Cape Cod',
  'the Poconos',
  'the Finger Lakes',
  'Rehoboth',
  'somewhere in Maine',
  'the Chesapeake',
  'a cabin in the Catskills',
]
const TWISTS = [
  'in a house with one bathroom',
  'the same week as a county fair',
  'with a hot tub nobody trusts',
  'forty minutes from the nearest store',
  'in a place with a very specific parking rule',
  'with a dock and a suspicious canoe',
  'next door to a wedding',
  'where the wifi is "coming soon"',
  'with a screened porch worth the whole trip',
  'and a dining table that seats six',
]
const CATCHES = [
  'and everyone drives separately',
  'and it rains Tuesday',
  'and someone forgets the sunscreen',
  'and check-in is at 4pm sharp',
  'and the group chat has opinions',
  'and two people arrive a day late',
  'and the grill runs out of propane',
  'and nobody wants to leave',
  'and the drive is longer than promised',
  'and it becomes the one everyone talks about',
]

function pick(list: string[]): string {
  return list[Math.floor(Math.random() * list.length)] ?? ''
}

function DestinationRoulette({ onWin }: { onWin: () => void }): JSX.Element {
  const [reels, setReels] = useState<[string, string, string] | null>(null)
  const [spinning, setSpinning] = useState(false)
  const timers = useRef<number[]>([])

  useEffect(() => () => timers.current.forEach((t) => window.clearTimeout(t)), [])

  function spin(): void {
    if (spinning) return
    setSpinning(true)
    timers.current.forEach((t) => window.clearTimeout(t))
    timers.current = []

    // Cycle visibly, then settle one reel at a time — the whole pleasure of a
    // slot machine is the third reel landing last.
    const cycle = window.setInterval(() => {
      setReels([pick(PLACES), pick(TWISTS), pick(CATCHES)])
    }, 70)

    const stop = window.setTimeout(() => {
      window.clearInterval(cycle)
      setReels([pick(PLACES), pick(TWISTS), pick(CATCHES)])
      setSpinning(false)
      onWin()
    }, 1400)
    timers.current.push(stop)
  }

  return (
    <section className="pz-card">
      <h2>Destination roulette</h2>
      <p className="pz-hint">
        Not a real proposal. The real ones are on <a href="/regions">Regions</a>, ranked by
        who has the worst drive.
      </p>
      <div className="pz-reels" aria-live="polite">
        {reels ? (
          <>
            <span className="pz-reel-1">{reels[0]}</span>
            <span className="pz-reel-2">{reels[1]}</span>
            <span className="pz-reel-3">{reels[2]}</span>
          </>
        ) : (
          <span className="pz-reel-2">Pull the lever</span>
        )}
      </div>
      <button type="button" className="pz-big" onClick={spin} disabled={spinning}>
        {spinning ? 'Spinning…' : 'Spin'}
      </button>
    </section>
  )
}

/* --------------------------------------------------------------- tap-tap -- */

type LightState = 'idle' | 'wait' | 'go' | 'foul' | 'done'

function TapTap({
  best,
  onBest,
  onWin,
}: {
  best: number
  onBest: (ms: number) => void
  onWin: () => void
}): JSX.Element {
  const [state, setState] = useState<LightState>('idle')
  const [ms, setMs] = useState(0)
  const goAt = useRef(0)
  const timer = useRef(0)

  useEffect(() => () => window.clearTimeout(timer.current), [])

  function arm(): void {
    setState('wait')
    setMs(0)
    // 1.4–4.5s, so it can never be learned as a rhythm.
    timer.current = window.setTimeout(
      () => {
        goAt.current = Date.now()
        setState('go')
      },
      1400 + Math.random() * 3100,
    )
  }

  function hit(): void {
    if (state === 'idle' || state === 'done' || state === 'foul') {
      arm()
      return
    }
    if (state === 'wait') {
      // Jumped the gun.
      window.clearTimeout(timer.current)
      setState('foul')
      return
    }
    const elapsed = Date.now() - goAt.current
    setMs(elapsed)
    setState('done')
    if (best === 0 || elapsed < best) {
      onBest(elapsed)
      onWin()
    }
  }

  const label =
    state === 'idle'
      ? 'Tap to start'
      : state === 'wait'
        ? 'Wait for it…'
        : state === 'go'
          ? 'TAP!'
          : state === 'foul'
            ? 'Too early. Tap to try again'
            : `${ms}ms — tap to go again`

  return (
    <section className="pz-card">
      <h2>Tap-Tap</h2>
      <p className="pz-hint">
        Wait for green, then tap. Tap early and it doesn&rsquo;t count &mdash; which is the
        hard part, and the whole game.
      </p>
      <p className="pz-score">Best {best === 0 ? '—' : `${best}ms`}</p>
      <button
        type="button"
        className={`pz-light${
          state === 'wait'
            ? ' pz-light-wait'
            : state === 'go'
              ? ' pz-light-go'
              : state === 'foul'
                ? ' pz-light-foul'
                : ''
        }`}
        onClick={hit}
        aria-live="polite"
      >
        {label}
      </button>
    </section>
  )
}

/* ------------------------------------------------------------- meowdoku -- */

function Meowdoku({
  best,
  onBest,
  onWin,
}: {
  best: number
  onBest: (seconds: number) => void
  onWin: () => void
}): JSX.Element {
  const [puzzle, setPuzzle] = useState<Puzzle | null>(null)
  const [grid, setGrid] = useState<Grid>([])
  const [startedAt, setStartedAt] = useState(0)
  const [elapsed, setElapsed] = useState(0)
  const [won, setWon] = useState(false)

  const deal = useCallback(() => {
    const next = generatePuzzle()
    setPuzzle(next)
    setGrid([...next.start])
    setStartedAt(0)
    setElapsed(0)
    setWon(false)
  }, [])

  useEffect(() => deal(), [deal])

  // The clock starts on the first tap, not on deal — nobody should be timed while
  // they are still reading the board.
  useEffect(() => {
    if (startedAt === 0 || won) return
    const id = window.setInterval(() => setElapsed(Math.floor((Date.now() - startedAt) / 1000)), 250)
    return () => window.clearInterval(id)
  }, [startedAt, won])

  function tap(i: number): void {
    if (!puzzle || won || puzzle.given.has(i)) return
    const at = startedAt === 0 ? Date.now() : startedAt
    if (startedAt === 0) setStartedAt(at)

    const next = [...grid]
    next[i] = cycle(next[i] ?? 0)
    setGrid(next)

    if (isSolved(next)) {
      const seconds = Math.max(1, Math.round((Date.now() - at) / 1000))
      setWon(true)
      setElapsed(seconds)
      if (best === 0 || seconds < best) {
        onBest(seconds)
        onWin()
      }
    }
  }

  const bad = conflicts(grid)

  return (
    <section className="pz-card">
      <h2>Meowdoku</h2>
      <p className="pz-hint">
        Every row, every column and every 2&times;2 box gets all four cats, once each. Tap a
        square to cycle through them.
      </p>
      <p className="pz-score">
        Best {best === 0 ? '—' : `${best}s`} · {won ? `Solved in ${elapsed}s` : `${elapsed}s`}
      </p>

      <div className="pz-meow" role="group" aria-label="Meowdoku board">
        {grid.map((v, i) => {
          const fixed = puzzle?.given.has(i) ?? false
          return (
            <button
              key={i}
              type="button"
              className={`pz-meow-cell${fixed ? ' pz-meow-fixed' : ''}${
                bad.has(i) ? ' pz-meow-bad' : ''
              }`}
              onClick={() => tap(i)}
              disabled={fixed || won}
              aria-label={`Row ${Math.floor(i / 4) + 1}, column ${(i % 4) + 1}: ${
                v === 0 ? 'empty' : CATS[v - 1]
              }`}
            >
              {v === 0 ? '' : CATS[v - 1]}
            </button>
          )
        })}
      </div>

      {won ? <p className="pz-meow-won">All four cats, everywhere. 🎉</p> : null}

      <button type="button" className="pz-btn" onClick={deal}>
        {won ? 'Another one' : 'New board'}
      </button>
    </section>
  )
}

/* ----------------------------------------------------------------- page -- */

export default function ExtraFunPage(): JSX.Element {
  const reduced = prefersReducedMotion()
  const [confettiOn, setConfettiOn] = useState(!reduced)
  const [disco, setDisco] = useState(false)
  const [burst, setBurst] = useState(0)
  const [bests, setBests] = useState<Bests>({ packed: 0, reaction: 0, meow: 0 })

  useEffect(() => setBests(loadBests()), [])

  const celebrate = useCallback(() => setBurst((b) => b + 1), [])

  const setBest = useCallback((patch: Partial<Bests>) => {
    setBests((prev) => {
      const next = { ...prev, ...patch }
      saveBests(next)
      return next
    })
  }, [])

  return (
    <>
      <div className={`pz-bg${disco ? ' pz-disco' : ''}`} aria-hidden="true" />
      <Confetti active={confettiOn} colors={PALETTE} burstKey={burst} />

      <main className={`pz${disco ? ' pz-disco' : ''}`}>
        <style href="spz-extrafun" precedence="spz">
          {CSS}
        </style>

        <header className="pz-sign">
          <div className="pz-bulbs" aria-hidden="true">
            {Array.from({ length: 11 }, (_, i) => (
              <span key={i} className="pz-bulb" style={{ animationDelay: `${i * 0.12}s` }} />
            ))}
          </div>
          <p className="pz-eyebrow">Saltz Party Zone</p>
          <h1 className="pz-title">Extra Fun</h1>
          <p className="pz-vacancy">Vacancy · open all year</p>
          <div className="pz-bulbs" aria-hidden="true">
            {Array.from({ length: 11 }, (_, i) => (
              <span key={i} className="pz-bulb" style={{ animationDelay: `${1.3 - i * 0.12}s` }} />
            ))}
          </div>
        </header>

        <div className="pz-controls">
          <button
            type="button"
            className="pz-btn"
            aria-pressed={confettiOn}
            onClick={() => setConfettiOn((v) => !v)}
          >
            Confetti {confettiOn ? 'on' : 'off'}
          </button>
          <button
            type="button"
            className="pz-btn"
            aria-pressed={disco}
            onClick={() => setDisco((v) => !v)}
          >
            Disco {disco ? 'on' : 'off'}
          </button>
          <button type="button" className="pz-btn pz-btn-cannon" onClick={celebrate}>
            Confetti cannon
          </button>
        </div>

        {reduced ? (
          <p className="pz-hint" style={{ textAlign: 'center' }}>
            Your device asks for reduced motion, so the confetti is switched off. Everything
            else still works.
          </p>
        ) : null}

        <div className="pz-games">
          <PackTheCar
            best={bests.packed}
            onBest={(packed) => setBest({ packed })}
            onWin={celebrate}
          />
          <TapTap
            best={bests.reaction}
            onBest={(reaction) => setBest({ reaction })}
            onWin={celebrate}
          />
          <Meowdoku best={bests.meow} onBest={(meow) => setBest({ meow })} onWin={celebrate} />
        </div>

        <DestinationRoulette onWin={celebrate} />

        <section className="pz-board">
          <h2>Records on this phone</h2>
          <div className="pz-row">
            <span>Most packed in 25 seconds</span>
            <span className="pz-odo">{String(bests.packed).padStart(4, '0')}</span>
          </div>
          <div className="pz-row">
            <span>Fastest tap</span>
            <span className="pz-odo">
              {bests.reaction === 0 ? '––––' : `${String(bests.reaction).padStart(4, '0')}ms`}
            </span>
          </div>
          <div className="pz-row">
            <span>Fastest Meowdoku</span>
            <span className="pz-odo">
              {bests.meow === 0 ? '––––' : `${String(bests.meow).padStart(4, '0')}s`}
            </span>
          </div>
          <p className="pz-hint" style={{ marginTop: '0.6rem' }}>
            Kept on this device only. Nothing here is sent anywhere, and none of it counts
            toward where we actually go.
          </p>
        </section>

        <p className="pz-foot">
          <a href="/">Back to the trip</a>
        </p>
      </main>
    </>
  )
}
